import { useEffect, useMemo, useState } from 'react'
import {
  ReviewOffer,
  disputedSteps,
  reviewOffer,
  type AdversarialRun,
  type DisputedStep,
} from './adversarialReview.ts'
import type { GuidedStep } from './guidedSteps.ts'

/**
 * Walking an adversarial review on the conferência itself (#541).
 *
 * The page already walks a conferência step by step. This narrows that walk
 * to the steps the latest review disputes -- the way the Correção view narrows
 * it to what was marked -- and says whether the page is in it.
 *
 * `walking` is asked *and* possible. A run that turns outdated under the
 * walk, or one with nothing that has a step, ends it without anybody pressing
 * anything: the page falls back to the conferência as it is, which is the
 * only honest thing to show once the notes no longer match the rows.
 */
export interface ReviewWalk {
  readonly walking: boolean
  /** The disputed steps of a run that can be opened. Empty otherwise. */
  readonly disputed: readonly DisputedStep[]
  /** Those steps alone, for the page to walk in place of its own. */
  readonly steps: readonly GuidedStep[]
  readonly begin: () => void
  readonly end: () => void
}

export function useReviewWalk(
  run: AdversarialRun | null | undefined,
  allSteps: readonly GuidedStep[],
): ReviewWalk {
  const [asked, setAsked] = useState(false)

  const disputed = useMemo(
    () =>
      run !== null && run !== undefined && reviewOffer(run) === ReviewOffer.Ready
        ? disputedSteps(allSteps, run.disagreements)
        : [],
    [run, allSteps],
  )
  const steps = useMemo(() => disputed.map((one) => one.step), [disputed])

  /*
   * A walk that ended on its own stays ended.
   *
   * Without this the request outlives the walk: run a review again from inside
   * one and the page goes back to an ordinary conferência while it runs -- and
   * minutes later, when the new run lands, turns read-only under whoever is by
   * then working on it. Walking again is a press, never a consequence.
   */
  useEffect(() => {
    if (asked && disputed.length === 0) setAsked(false)
  }, [asked, disputed.length])

  return {
    walking: asked && disputed.length > 0,
    disputed,
    steps,
    begin: () => setAsked(true),
    end: () => setAsked(false),
  }
}
